import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Crosshair, Home, Pencil, ShieldCheck } from "lucide-react";

import { HomeMap } from "@/components/me/HomeMap";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/auth/AuthContext";
import { useGeolocation } from "@/hooks/useGeolocation";
import { ApiError } from "@/lib/api";
import { formatDateTimeIn } from "@/lib/datetime";
import { employeeApi, type HomeLocation } from "@/lib/employee-api";
import { hasGoogleMapsKey } from "@/lib/google-maps";
import {
  HOME_RADIUS_MAX_M,
  HOME_RADIUS_MIN_M,
  formFromHome,
  hasHomeLocation,
  homeLocationChanged,
  organisationDefaultRadius,
  parseHomeLocationForm,
  type HomeLocationForm,
  type SavedHomeLocation,
} from "@/lib/home-location";

/**
 * The employee's own profile, and the one thing on it they can change: their
 * home location.
 *
 * This is **self-service only** by rule (BR-063) — HR cannot set it for
 * someone — so before this screen existed a web-only employee could never be
 * classified WFH at all, however the tenant and site policies were configured.
 *
 * A saved home location shows read-only, with Edit (BUG-2026-015). Every
 * change notifies HR, so the form offers Save only for a valid change, and
 * Cancel puts the saved values back.
 *
 * Coordinates shown here are the employee's own and nobody else's; the admin
 * side keeps them HR-gated.
 */

/** Times on this page are shown in Malaysian time. */
const TZ = "Asia/Kuala_Lumpur";

/** CONTRACT -> Contract. Enum values are shouted; a profile row should not be. */
function titleCase(v: string | null | undefined): string | null {
  if (!v) return null;
  return v
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** A label and its value, or "Not set" — never a blank row. */
function Row({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className={`min-w-0 text-right break-words ${value ? "font-medium" : "text-muted-foreground"}`}>
        {value || "Not set"}
      </span>
    </div>
  );
}

/** A saved home location, read-only: where it is, how far it reaches, when it changed. */
function HomeLocationSummary({ home, onEdit }: { home: SavedHomeLocation; onEdit: () => void }) {
  return (
    <>
      {hasGoogleMapsKey && (
        <HomeMap latitude={home.latitude} longitude={home.longitude} radiusM={home.effectiveRadiusM} />
      )}
      <div className="space-y-1 text-sm">
        <Row label="Address" value={home.address} />
        <Row label="Coordinates" value={`${home.latitude}, ${home.longitude}`} />
        <Row
          label="Radius"
          value={
            home.radiusM != null
              ? `${home.radiusM} m`
              : `${home.effectiveRadiusM} m — your organisation's default`
          }
        />
        <Row label="Saved" value={home.updatedAt ? formatDateTimeIn(home.updatedAt, TZ) : null} />
      </div>
      <Button variant="outline" onClick={onEdit}>
        <Pencil className="mr-1.5 h-4 w-4" />
        Edit
      </Button>
    </>
  );
}

export default function MyProfile() {
  const { employee } = useAuth();
  const { toast } = useToast();
  const geo = useGeolocation();

  const [home, setHome] = useState<HomeLocation | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A saved location shows read-only until Edit; with none saved yet, the
  // form is all there is.
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<HomeLocationForm>(() => formFromHome(null));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const h = await employeeApi.homeLocation();
      setHome(h);
      setForm(formFromHome(h));
      setEditing(!hasHomeLocation(h));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load your profile");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /** Open the form on the saved values, or close it and drop the edit. */
  const setEditingFromSaved = (open: boolean) => {
    setForm(formFromHome(home));
    geo.reset();
    setEditing(open);
  };

  const fillFromCurrentLocation = async () => {
    const pos = await geo.locate();
    if (!pos) return;
    setForm((f) => ({
      ...f,
      latitude: pos.latitude.toFixed(6),
      longitude: pos.longitude.toFixed(6),
    }));
  };

  const parsed = parseHomeLocationForm(form);
  const changed = homeLocationChanged(form, home);
  // Only a change is worth saving: each one notifies HR (BR-063).
  const canSave = parsed.body != null && changed && !saving;
  const orgDefault = organisationDefaultRadius(home);

  const save = async () => {
    if (!parsed.body || !changed) return;
    setSaving(true);
    try {
      const saved = await employeeApi.setHomeLocation(parsed.body);
      setHome(saved);
      setForm(formFromHome(saved));
      geo.reset();
      setEditing(false);
      toast({
        title: "Home location saved",
        description: "Your HR team is notified whenever this changes.",
      });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Could not save your home location",
        description: e instanceof ApiError ? e.message : "Something went wrong",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Skeleton className="h-64 w-full" />;

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Couldn't load your profile</AlertTitle>
        <AlertDescription className="flex items-center gap-3">
          {error}
          <Button size="sm" variant="outline" onClick={() => void load()}>
            Try again
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const DETAILS: Array<{ label: string; value: string | null | undefined }> = [
    { label: "Name", value: employee?.name },
    { label: "Employee code", value: employee?.empCode },
    { label: "Email", value: employee?.email },
    { label: "Phone", value: employee?.phone },
    { label: "Job title", value: employee?.role },
    { label: "Staff category", value: titleCase(employee?.employmentType) },
    { label: "Department", value: employee?.departmentName },
    { label: "Work location", value: employee?.primarySite?.name },
    { label: "Shift", value: employee?.shift?.name },
    { label: "Reporting manager", value: employee?.manager?.name },
    { label: "Organisation", value: employee?.tenantName },
    {
      // Last activity, not a sign-in: despite its name, the API refreshes
      // `lastMobileLoginAt` on every authenticated employee request, from the
      // app or this portal. Labelled "Last mobile sign-in" it read as a session
      // that never expired (BUG-2026-009).
      label: "Last active",
      value: employee?.lastMobileLoginAt ? formatDateTimeIn(employee.lastMobileLoginAt, TZ) : null,
    },
  ];

  // While editing, the map follows the form once its coordinates read. A
  // blank radius is the organisation's default — drawn only when known.
  const preview = parsed.body;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Your details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1 text-sm">
          {DETAILS.map(({ label, value }) => (
            <div key={label} className="flex justify-between gap-3">
              <span className="shrink-0 text-muted-foreground">{label}</span>
              {/* A field the organisation has not filled in reads as "Not set",
                  never as a blank row — an empty value beside a label looks
                  like the page failed to load. */}
              <span
                className={`truncate ${value ? "font-medium" : "text-muted-foreground"}`}
              >
                {value || "Not set"}
              </span>
            </div>
          ))}
          <p className="pt-2 text-xs text-muted-foreground">
            To correct any of these, contact your HR team.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Home className="h-4 w-4" />
            Home location
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Used to recognise a check-in from home as working from home. Only you can set
            this, and your HR team is notified when it changes.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {!editing && hasHomeLocation(home) ? (
            <HomeLocationSummary home={home} onEdit={() => setEditingFromSaved(true)} />
          ) : (
            <>
              {!hasHomeLocation(home) && (
                <p className="text-sm text-muted-foreground">
                  You haven't set a home location yet. Use your current location, or enter
                  the coordinates.
                </p>
              )}

              <Button
                variant="outline"
                onClick={() => void fillFromCurrentLocation()}
                disabled={geo.locating}
              >
                <Crosshair className="mr-1.5 h-4 w-4" />
                {geo.locating ? "Locating…" : "Use my current location"}
              </Button>

              {geo.error && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{geo.error.message}</AlertDescription>
                </Alert>
              )}

              {hasGoogleMapsKey && preview && (
                <HomeMap
                  latitude={preview.latitude}
                  longitude={preview.longitude}
                  radiusM={preview.radiusM ?? orgDefault}
                />
              )}

              <div className="grid grid-cols-2 gap-3">
                <Field label="Latitude">
                  <Input
                    value={form.latitude}
                    inputMode="decimal"
                    onChange={(e) => setForm((f) => ({ ...f, latitude: e.target.value }))}
                  />
                </Field>
                <Field label="Longitude">
                  <Input
                    value={form.longitude}
                    inputMode="decimal"
                    onChange={(e) => setForm((f) => ({ ...f, longitude: e.target.value }))}
                  />
                </Field>
              </div>

              <Field label="Address" hint="Optional — helps you recognise the pin later">
                <Input
                  value={form.address}
                  maxLength={300}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                />
              </Field>

              <Field
                label="Radius (metres)"
                hint={
                  orgDefault != null
                    ? `Blank uses your organisation's default of ${orgDefault} m. Allowed: ${HOME_RADIUS_MIN_M}–${HOME_RADIUS_MAX_M} m.`
                    : `Blank uses your organisation's default. Allowed: ${HOME_RADIUS_MIN_M}–${HOME_RADIUS_MAX_M} m.`
                }
              >
                <Input
                  value={form.radiusM}
                  inputMode="numeric"
                  placeholder={orgDefault != null ? String(orgDefault) : "Organisation default"}
                  onChange={(e) => setForm((f) => ({ ...f, radiusM: e.target.value }))}
                />
              </Field>

              {changed && parsed.problem && (
                <p className="text-sm text-destructive">{parsed.problem}</p>
              )}

              <div className="flex flex-wrap gap-2">
                <Button onClick={() => void save()} disabled={!canSave}>
                  {saving ? "Saving…" : "Save home location"}
                </Button>
                {hasHomeLocation(home) && (
                  <Button variant="ghost" onClick={() => setEditingFromSaved(false)} disabled={saving}>
                    Cancel
                  </Button>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4" />
            Account and security
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          <p>
            Your password and sign-in security are managed by your organisation's
            sign-in provider, not by GeoKerja — change them from the account menu
            where you signed in. Sign out using the button at the top of this page.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
